import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    Signal,
    computed,
    inject,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormatDurationPipe } from '@dxp/core/pipes';
import { TelXLDialpadComponent } from '@dxp/elements';
import { Duration } from 'date-fns';
import { VoiceIVRService, VoiceService } from '@dxp/core/services';
import { IVoiceAction } from '@dxp/core/models';
import { VoiceApiService } from '@dxp/core/api';
import { VoiceCallButtonComponent } from '../voice-call-buttons/voice-call-button.component';
import { VoiceCallInfoComponent } from '../voice-call-info/voice-call-info.component';
import { VoiceCallService } from '../../services/voice-call.service';
import { VoiceStore } from '../../store/voice.store';

@Component({
    selector: 'voice-call',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        VoiceCallButtonComponent,
        VoiceCallInfoComponent,
        TelXLDialpadComponent,
        FormatDurationPipe,
    ],
    templateUrl: './voice-call.component.html',
    styleUrl: './voice-call.component.scss',
})
export class VoiceCallComponent implements OnDestroy {
    protected voiceStore = inject(VoiceStore);

    readonly #voiceCallService = inject(VoiceCallService);
    readonly #voiceService = inject(VoiceService);
    readonly #voiceIVRService = inject(VoiceIVRService);
    readonly #voiceApiService = inject(VoiceApiService);

    readonly callDescription: Signal<string> =
        this.#voiceCallService.callDescription;
    readonly conversation = computed(() => this.#voiceService.conversation());
    readonly callTime: Signal<Duration> = this.#voiceCallService.callTime;

    readonly transfer: Signal<boolean> = this.#voiceCallService.transfer;
    readonly enquiry: Signal<boolean> = this.#voiceCallService.enquiry;
    readonly ivr: Signal<boolean> = this.#voiceCallService.ivr;
    readonly keypad: Signal<boolean> = this.#voiceCallService.keypad;
    readonly onHold = signal(false);

    readonly isOnHold = computed(() => {
        if (this.agentInitiatedToAgent()) return this.onHold();
        return this.voiceStore.activeVoiceTaskOnHold().onHold;
    });

    readonly displayContacts: Signal<boolean> = computed(
        () => this.#voiceService.displayContacts,
    );
    readonly inEnquiryCall: Signal<boolean> = computed(() =>
        this.#voiceService.enquiryCallDisplayed(),
    );
    readonly workItem = computed(() => this.#voiceService.workItem());
    readonly customerName = computed(() => {
        if (this.conversation()?.isInternalCall) {
            return 'Internal';
        }
        const customer = this.workItem()?.primaryConversationCustomer;
        return customer
            ? `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim()
            : 'Unknown';
    });

    readonly panelHighlighted = computed(() =>
        this.#voiceService.panelHighlighted(),
    );
    readonly isOutbound = computed(() => this.workItem()?.isOutbound);

    readonly isInternal = computed(() => this.conversation()?.isInternalCall);

    readonly ivrs = computed(() => {
        if (this.isOutbound()) {
            return [];
        }
        return this.#voiceIVRService.ivrs() ?? [];
    });
    agentName = computed<string>(
        () => this.conversation()?.callerIdName ?? 'Unknown agent',
    );

    readonly agentInitiatedToAgent = computed(() =>
        this.#voiceService.agentInitiatedToAgent(),
    );

    readonly enquiryDisabled = computed(
        () =>
            (this.displayContacts() &&
                (this.transfer() || this.inEnquiryCall() || this.ivr())) ||
            (this.isInternal() &&
                !this.#voiceService.agentInitiatedToAgent()) ||
            (this.workItem()?.isEnquiryCall ?? false),
    );
    readonly transferDisabled = computed(
        () =>
            (this.displayContacts() && (this.enquiry() || this.ivr())) ||
            (this.isInternal() &&
                !this.#voiceService.agentInitiatedToAgent()) ||
            (this.workItem()?.isEnquiryCall ?? false),
    );
    readonly ivrDisabled = computed(
        () =>
            (this.displayContacts() && (this.enquiry() || this.transfer())) ||
            this.ivrs().length === 0 ||
            this.isInternal() ||
            (this.workItem()?.isEnquiryCall ?? false),
    );

    holdButtonClicked = computed(() => {
        if (this.inEnquiryCall()) {
            return true;
        }

        return this.voiceStore.activeVoiceTaskOnHold().onHold;
    });

    readonly opOnHold = computed(() => this.#voiceService.opOnHold());

    holdButtonDisabled = computed(() => {
        if (this.inEnquiryCall()) {
            return true;
        }

        if (this.agentInitiatedToAgent()) {
            return false;
        }

        return (
            this.voiceStore.activeVoiceTaskOnHold().disabled ||
            !!this.conversation()?.isInternalCall
        );
    });

    constructor() {
        /**
         * Initialize the reactive method - When the VoiceTasks signal changes
         */
        this.voiceStore.processActions(
            this.voiceStore.voiceAction as unknown as IVoiceAction,
        );
    }

    btnClicked(title: string) {
        this.#voiceCallService.handleButtonClick(title);
    }

    dialpadClick(digit: string) {
        this.#voiceCallService.sendDtmf(digit);
    }

    endCall() {
        this.#voiceCallService.endCall();
    }

    toggleHoldButton() {
        const isCurrentlyOnHold = this.isOnHold();
        const newHoldState = !isCurrentlyOnHold;

        this.onHold.set(newHoldState);

        this.#voiceApiService.changeCallState({
            conversationId: this.conversation()?.conversationId ?? '',
            callState: newHoldState ? 'Hold' : 'Resume',
        });
    }

    ngOnDestroy() {
        this.#voiceService.contacts.set({
            ...this.#voiceService.contacts(),
            display: false,
        });
    }
}
